import {AlertCircle, CheckCircle, Clock, ExternalLink, Package} from 'lucide-react';
import {Alert, CircularProgress} from '@mui/material';
import {PortalShell} from '../../shared/ui';
import {useLicencas} from '../Licencas/useLicencas';
import {useOpcionaisContratados} from '../Licencas/useOpcionaisContratados';
import OpcionaisContratados from '../Licencas/OpcionaisContratados';

const tipoConfig = {
    PAGO: {label: 'Pago', icon: CheckCircle},
    TRIAL: {label: 'Trial', icon: Clock},
    GRATUITO: {label: 'Gratuito', icon: Package},
};

const statusConfig = {
    ATIVA: {label: 'Ativa', className: 'bf-pill bf-pill--success'},
    INATIVA: {label: 'Inativa', className: 'bf-pill bf-pill--muted'},
    BLOQUEADA: {label: 'Bloqueada', className: 'bf-pill bf-pill--danger'},
    CANCELADA: {label: 'Cancelada', className: 'bf-pill bf-pill--muted'},
    TRIAL: {label: 'Trial', className: 'bf-pill bf-pill--warning'},
};

function formatDate(dateString) {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return Number.isNaN(date.getTime()) ? dateString : date.toLocaleDateString('pt-BR');
}

function getDaysUntil(dateString) {
    if (!dateString) return 0;
    const date = new Date(dateString);
    const today = new Date();
    return Math.ceil((date.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
}

export default function SistemasPage() {
    const data = useLicencas();
    const opcionais = useOpcionaisContratados(data.rows.map((license) => license.id));

    return (
        <PortalShell>
            <div className="bf-page">
                <div>
                    <h1 className="bf-page__title">Sistemas</h1>
                    <p className="bf-page__subtitle">Gerencie seus sistemas contratados e assinaturas ativas.</p>
                </div>

                {data.error ? <Alert severity="warning">{data.error}</Alert> : null}

                {data.loading ? (
                    <div className="bf-empty"><CircularProgress /></div>
                ) : (
                    <>
                        <div className="bf-grid-3">
                            {data.rows.map((license) => {
                                const raw = license.raw || {};
                                const typeKey = raw?.tipo || 'PAGO';
                                const statusKey = raw?.status || 'ATIVA';
                                const TypeIcon = (tipoConfig[typeKey] || tipoConfig.PAGO).icon;
                                const daysUntilExpiry = getDaysUntil(raw?.validade_fim);
                                const isExpiringSoon = daysUntilExpiry <= 30 && daysUntilExpiry > 0;

                                return (
                                    <div key={license.id} className="bf-card bf-card--hover">
                                        <div className="bf-card__header">
                                            <div style={{display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 12}}>
                                                <div style={{flex: 1}}>
                                                    <h3 className="bf-system-title">{license.system}</h3>
                                                    <p className="bf-system-desc">{raw?.descricao || raw?.plano?.descricao || 'Sistema contratado para sua operação.'}</p>
                                                </div>
                                            </div>
                                            <div className="bf-stack-1">
                                                <span className={(statusConfig[statusKey] || statusConfig.ATIVA).className}>{(statusConfig[statusKey] || statusConfig.ATIVA).label}</span>
                                                <span className="bf-pill bf-pill--muted"><TypeIcon size={14} />{(tipoConfig[typeKey] || tipoConfig.PAGO).label}</span>
                                                <span className="bf-pill bf-pill--primary">{license.plan}</span>
                                            </div>
                                        </div>

                                        <div className="bf-card__body" style={{paddingTop: 24}}>
                                            <div className="bf-stack-3">
                                                {isExpiringSoon ? (
                                                    <div className="bf-note bf-note--warning">
                                                        <div className="bf-row" style={{gap: 8, alignItems: 'flex-start'}}>
                                                            <AlertCircle size={16} style={{marginTop: 2, flexShrink: 0}} />
                                                            <div style={{fontSize: 12}}><strong>Atenção:</strong> Vence em {daysUntilExpiry} dias</div>
                                                        </div>
                                                    </div>
                                                ) : null}

                                                <div>
                                                    <div style={{fontSize: 12, color: 'var(--bf-muted-foreground)', marginBottom: 8, fontWeight: 500}}>Opcionais Contratados</div>
                                                    <OpcionaisContratados estado={opcionais[license.id]} />
                                                </div>

                                                <div className="bf-divider-top" style={{display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 12}}>
                                                    <div>
                                                        <div style={{fontSize: 12, color: 'var(--bf-muted-foreground)', marginBottom: 4}}>Validade</div>
                                                        <div style={{fontSize: 14, fontWeight: 500}}>{formatDate(raw?.validade_fim)}</div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="bf-card__footer">
                                            <button className="bf-button bf-button--primary bf-button--sm bf-button--full" disabled={statusKey !== 'ATIVA'}>
                                                <span>Acessar Sistema</span>
                                                <ExternalLink size={14} />
                                            </button>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                        {data.rows.length === 0 ? (
                            <div className="bf-card"><div className="bf-empty"><Package size={48} style={{marginBottom: 16}} /><div style={{fontWeight: 600, fontSize: 18, marginBottom: 8}}>Nenhum sistema contratado</div><div>Entre em contato com nossa equipe comercial para contratar sistemas.</div></div></div>
                        ) : null}
                    </>
                )}
            </div>
        </PortalShell>
    );
}
